import React, { useState, useEffect } from 'react';
import { Joystick } from './CommonUI';
import { ASSETS } from '../assetManifest';

const HelpModal = ({ onClose }) => (
  <div className="absolute inset-0 flex items-center justify-center z-[3000]">
    <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
    <div 
      className="relative w-[300px] bg-center bg-no-repeat p-4"
      style={{
        backgroundImage: `url(${ASSETS.ui.popup.path})`,
        height: '500px',
        paddingTop: '60px',
        backgroundSize: '100% 100%'
      }}
    >
      <div className="text-center mb-4">
        <h2 className="text-[#2A4163] text-xl font-bold">Game Guide</h2>
      </div>
      
      <div className="px-4 overflow-y-auto h-[350px]">
        <div className="bg-black bg-opacity-40 p-4 rounded-lg">
          <div className="mb-6">
            <div className="flex items-center justify-center gap-4 mb-2">
              <img src={ASSETS.environment.bush.path} alt="Bush" className="w-10 h-10" />
              <span className="text-white text-2xl">→</span>
              <img src={ASSETS.items.fruit.path} alt="Fruit" className="w-10 h-10" />
              <span className="text-white text-2xl">→</span>
              <img src={ASSETS.items.hunger.path} alt="Hunger" className="w-10 h-10" />
            </div>
            <div className="text-center text-white text-sm">
              Gather fruits to survive
            </div>
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-center gap-4 mb-2">
              <img src={ASSETS.environment.tree.path} alt="Tree" className="w-10 h-10" />
              <span className="text-white text-2xl">→</span>
              <img src={ASSETS.items.wood.path} alt="Wood" className="w-10 h-10" />
              <span className="text-white text-2xl">→</span>
              <img src={ASSETS.ui.fire.path} alt="Fire" className="w-10 h-10" />
            </div>
            <div className="text-center text-white text-sm">
              Build fires to stay warm
            </div>
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-center gap-4 mb-2">
              <img src={ASSETS.items.snowflake.path} alt="Cold" className="w-10 h-10" />
              <span className="text-white text-2xl">+</span>
              <img src={ASSETS.items.hunger.path} alt="Hunger" className="w-10 h-10" />
              <span className="text-white text-2xl">→</span>
              <span className="text-red-500 text-2xl">☠</span>
            </div>
            <div className="text-center text-white text-sm">
              Don't freeze or starve!
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={onClose}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        style={{
          backgroundImage: `url(${ASSETS.ui.button.path})`,
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          width: '100px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <span className="text-white">Close</span>
      </button>
    </div>
  </div>
);

const HUD = ({ gameState, currentUserId }) => {
  const [showHelp, setShowHelp] = useState(true);  // Changed from false to true
  const player = gameState.entities[currentUserId];
  const timeRemaining = Math.ceil(gameState.timeRemaining / 1000);
  
  if (!player) return null;

  return (
    <>
      <div
        className="absolute top-0 left-0 right-0 w-[390px] h-[97px] mx-auto"
        style={{ userSelect: 'none' }}
      >
        <div className="flex w-[330px] gap-[16px] justify-center items-center relative z-[2] mt-[3px] mx-0 mb-0 ml-[25px]">
          <span className="flex w-[112px] h-[26px] justify-start items-center font-['Asap'] text-[16px] font-semibold text-white">
            {player.name}
          </span>
          
          <div className="w-[0.5px] h-[32px] bg-cover" style={{backgroundImage: `url(${ASSETS.ui.verticalDivider.path})`}} />
          
          <div className="flex items-center gap-2">
            <img src={ASSETS.items.wood.path} alt="Wood" className="w-[36px] h-[34px]" />
            <span className="font-['Asap'] text-[16px] font-semibold text-white text-center w-[25px]">
              {player.logs}
            </span>
          </div>

          <div className="w-[0.5px] h-[32px] bg-cover" style={{backgroundImage: `url(${ASSETS.ui.verticalDivider.path})`}} />
          
          <div className="flex items-center gap-2">
            <img src={ASSETS.items.fruit.path} alt="Fruit" className="w-[36px] h-[34px]" />
            <span className="font-['Asap'] text-[16px] font-semibold text-white text-center w-[25px]">
              {player.fruits}
            </span>
          </div>
        </div>

        <div className="w-full h-[47px] relative z-[20] mt-[15px]">
          <div className="flex items-center gap-2 ml-[24px]">
            <img src={ASSETS.items.snowflake.path} alt="Cold" className="w-[20px] h-[20px]" />
            <div className="w-[112px] h-[10px] bg-cover" style={{backgroundImage: `url(${ASSETS.ui.meterBackground.path})`}}>
              <div
                className="h-full"
                style={{ width: `${player.cold}%`, backgroundColor: '#5FD0EA' }}
              />
            </div>
          </div>

          <div className="absolute left-1/2 transform -translate-x-1/2 flex flex-col items-center">
            <div className="w-[51px] h-[51px] rounded-full flex items-center justify-center bg-cover" style={{backgroundImage: `url(${ASSETS.ui.timerBackground.path})`}}>
              <span className="font-['Asap'] text-[16px] font-semibold text-white">
                {timeRemaining}
              </span>
            </div>
          </div>

          <div className="absolute right-[24px] top-0 flex items-center gap-2">
            <img src={ASSETS.items.hunger.path} alt="Hunger" className="w-[20px] h-[20px]" />
            <div className="w-[112px] h-[10px] bg-cover" style={{backgroundImage: `url(${ASSETS.ui.meterBackground.path})`}}>
              <div
                className="h-full rounded"
                style={{ width: `${player.hunger}%`, backgroundColor: '#FB7A59' }}
              />
            </div>
          </div>
        </div>

        <div 
          className="absolute top-0 left-0 w-[390px] h-[97px] bg-cover z-[1]"
          style={{backgroundImage: `url(${ASSETS.ui.hudBackground.path})`}}
        />

        <button
          onClick={() => setShowHelp(true)}
          className="fixed bottom-4 left-4 z-[25] w-[40px] h-[40px] rounded-full flex items-center justify-center text-white font-bold"
          style={{
            backgroundColor: 'rgba(8,84,203,0.8)',
            border: '2px solid white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.5)'
          }}
        >
          ?
        </button>
      </div>

      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
    </>
  );
};

const GameOverModal = ({ results, onFinish }) => {
  return (
    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-white p-4 rounded-lg text-black">
        <h2 className="text-xl font-bold mb-4">Game Over!</h2>
        <div className="mb-4">
          <p>Survivors: {results.winners.join(', ') || 'None'}</p>
          <p>Perished: {results.losers.join(', ') || 'None'}</p>
        </div>
        <button
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          onClick={onFinish}
        >
          OK
        </button>
      </div>
    </div>
  );
};

const ActionButton = ({ label, emoji, onClick, disabled = false, style = {}, costIcon, cost }) => (
  <div 
    className="absolute flex flex-col items-center" 
    style={{ height: '90px', ...style }}
  >
    <div
      className={`flex items-center justify-center ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      style={{
        width: '100px',
        height: '50px',
        backgroundImage: `url(${ASSETS.ui.button.path})`,
        backgroundSize: 'contain',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 10px',
        userSelect: 'none'
      }}
      onClick={disabled ? undefined : (e) => {
        e.preventDefault();
        onClick(e);
      }}
    >
      <div className="flex items-center justify-center gap-1">
        {emoji.startsWith('assets/') ? (
          <img src={emoji} alt={label} className="w-[16px] h-[16px]" />
        ) : (
          <img src={emoji} alt={label} className="w-[16px] h-[16px]" />
        )}
        <div className="text-white text-sm" style={{ textShadow: '1px 1px 2px black' }}>{label}</div>
      </div>
    </div>
    <div className="flex items-center mt-1 text-sm font-bold" style={{ color: '#00409F' }}>
      <span>Cost: </span>
      <img src={costIcon} alt="cost" className="w-[12px] h-[12px] mx-1" />
      <span>{cost}</span>
    </div>
  </div>
);

function GameUI({ gameState, gameInterface, onFinishGame }) {
  const [, forceUpdate] = useState();
  const player = gameState.entities[gameInterface.userId];

  const handleMove = (dx, dy) => {
    gameInterface.sendGameEvent('joystick_move', {dx, dy, userId: gameInterface.userId});
  };

  const handleEat = (e) => {
    e.preventDefault();
    gameInterface.sendGameEvent('eat', {userId: gameInterface.userId});
  };

  const handleBuildFire = (e) => {
    e.preventDefault();
    gameInterface.sendGameEvent('build_fire', {userId: gameInterface.userId});
  };

  useEffect(() => {
    const interval = setInterval(() => {
      forceUpdate({});
    }, 10);
    return () => clearInterval(interval);
  }, []);

  if (!player || player.isDead) return null;

  const canEat = player.fruits > 0;
  const canBuildFire = player.logs >= 5;

  return (
    <>
      <HUD gameState={gameState} currentUserId={gameInterface.userId} />
      
      <Joystick onMove={handleMove} position={{ bottom: '20px', right: '20px' }} />
      
      <ActionButton
        label="Eat"
        emoji={ASSETS.ui.eat.path}
        onClick={handleEat}
        disabled={!canEat}
        costIcon={ASSETS.items.fruit.path}
        cost="1"
        style={{ 
          bottom: '160px', 
          left: '20px',
          opacity: canEat ? 1 : 0,
          pointerEvents: canEat ? 'auto' : 'none'
        }}
      />
      
      <ActionButton
        label="Build Fire"
        emoji={ASSETS.ui.fire.path}
        onClick={handleBuildFire}
        disabled={!canBuildFire}
        costIcon={ASSETS.items.wood.path}
        cost="5"
        style={{ 
          bottom: '70px', 
          left: '20px',
          opacity: canBuildFire ? 1 : 0,
          pointerEvents: canBuildFire ? 'auto' : 'none'
        }}
      />

      {gameState.isGameOver && (
        <GameOverModal
          results={gameState.results}
          onFinish={onFinishGame}
        />
      )}
    </>
  );
}

export default GameUI;
